<script setup lang="ts">
import { computed, ref } from "vue";
import routeManager from "@/router/route-manager";
import { useMenuPermissions } from "@/composables/menu-permissions";
import { useApplicationStore } from "@/stores/Application";
import { useUserStore } from "@/stores/User";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";

const appStore = useApplicationStore();

const {
  showMyLibrary,
  showProfileDropdown,
} = useMenuPermissions();
const { userFullName } = storeToRefs( useUserStore() );

const logoutModal = ref< HTMLDialogElement >();
const nuclearOptionModal = ref< HTMLDialogElement >();

const route = useRoute();
const showReset = computed( () => !!route.query.reset )

function hideMenuOnBlur( event: MouseEvent ) {
  if ( event.target ) {
    ( event.target as Element ).removeAttribute( "open" );
  }
}

function showMenuOnFocus( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}

function showLogoutModal() {
  logoutModal.value?.showModal();
}

function hideModal() {
  logoutModal.value?.close();
}

function logout() {
  logoutModal.value?.close();
  appStore.logoutAndGoHome().then();
}

async function resetData() {
  await appStore.nukeItFromOrbitItsTheOnlyWayToBeSure();
}

function showResetModal() {
  nuclearOptionModal.value?.showModal();
}
</script>

<template>
  <details id="user-menu" @mouseenter="showMenuOnFocus" @mouseleave="hideMenuOnBlur" class="dropdown dropdown-end dropdown-hover hidden lg:block">
    <summary class="btn btn-sm btn-ghost text-base-content font-normal">
      <span class="flex items-center gap-2 text-base-content text-xs lg:text-sm">
        <i class="pi pi-user hidden lg:inline"></i>
        {{ userFullName }}
      </span>
    </summary>
    <ul class="menu dropdown-content w-64" @click="hideMenuOnBlur">
      <li v-if="showProfileDropdown">
        <router-link :to="routeManager.profileBasePath()">Profile</router-link>
      </li>
      <li v-if="showMyLibrary">
        <router-link :to="routeManager.myLibraryPath()">My Library</router-link>
      </li>
      <li>
        <router-link :to="routeManager.generalFeedbackPath()">Submit Feedback</router-link>
      </li>
        <li v-if="showReset" class="text-jade mb-4" @click.prevent="showResetModal">
          <span>
            <i class="pi pi-refresh"></i>
            Reset Data
          </span>
        </li>
      <hr class="my-2">
      <li @click.prevent="showLogoutModal">
        <div>Log Out</div>
      </li>
    </ul>
  </details>

  <!-- Mobile menu -->
  <details class="lg:hidden">
    <summary class="font-bold">Account</summary>
    <ul>
      <li v-if="showProfileDropdown">
        <router-link :to="routeManager.profileBasePath()">Profile</router-link>
      </li>
      <li v-if="showMyLibrary">
        <router-link :to="routeManager.myLibraryPath()">My Library</router-link>
      </li>
      <li>
        <router-link :to="routeManager.generalFeedbackPath()">Submit Feedback</router-link>
      </li>
      <li>
        <div @click.prevent="showLogoutModal" class="font-medium">Log Out</div>
      </li>
    </ul>
  </details>

  <!-- Logout Modal -->
  <dialog id="logoutModal" ref="logoutModal" class="modal">
    <div class="modal-box">
      <form method="dialog" @click="hideModal">
        <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
      </form>
      <h3 class="font-bold text-lg">Log Out</h3>
      <p class="py-4">Are you sure you want to log out?</p>
      <div class="w-full flex space-x-2">
        <form method="dialog" @click="hideModal" class="w-1/2">
          <button class="btn w-full">Cancel</button>
        </form>
        <div role="button" tabindex="0" class="btn btn-primary w-1/2" @click="logout">Log Out</div>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop" @click.prevent="hideModal">
      <button>close</button>
    </form>
  </dialog>

  <!-- Nuclear Option Modal -->
  <dialog id="nuclearOptionModal" ref="nuclearOptionModal" class="modal">
    <div class="modal-box">
      <form method="dialog" @click="hideModal">
        <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
      </form>
      <h3 class="font-bold text-lg">Reset Local Data</h3>
      <p class="py-4">By clicking the 'Reset Data' button, this action will:</p>
      <ul>
        <li><i class="pi pi-circle-on pr-2"></i>Log you out</li>
        <li><i class="pi pi-circle-on pr-2"></i>Clear all locally stored data for this application</li>
        <li><i class="pi pi-circle-on pr-2"></i>Will require a re-sync with the server</li>
      </ul>
      <p class="py-4">Do you wish to continue?</p>
      <div class="w-full flex space-x-2">
        <form method="dialog" @click="hideModal" class="w-1/2">
          <button class="btn w-full">Cancel</button>
        </form>
        <div role="button" tabindex="0" class="btn btn-primary w-1/2" @click="resetData">
          <span>
            <i class="pi pi-refresh"></i>
            Reset Data
          </span>
        </div>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop" @click.prevent="hideModal">
      <button>close</button>
    </form>
  </dialog>

</template>

